import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../api";
import ErrorState from "./ErrorState";
import { Avatar } from "./EndomarketingPecas";
import {
  type Colaborador,
  MES_CURTO,
  compactar,
  norm,
  temTelefone,
} from "../endomarketing-utils";
import { IconSearch, IconUpload, IconTrash, IconCheck } from "../icons";
import { usePodeExcluir } from "../permissoes";
import { ordenarPorNome, ordenarTextos } from "../ordem";

/**
 * FOTOS DA EQUIPE — o mural do RH.
 *
 * Duas coisas por pessoa: a FOTO de perfil (usada no cartão de aniversário e
 * nas listas) e a DATA DE NASCIMENTO, que é o que faz alguém aparecer nos
 * aniversariantes. Ambas gravam no próprio cadastro — não existe uma segunda
 * lista de pessoas para manter.
 *
 * Os filtros são de trabalho, não enfeite: "sem foto" e "sem nascimento" são
 * exatamente as filas que o RH precisa zerar para o resto do Endomarketing
 * funcionar.
 */

type Filtro = "todos" | "sem-foto" | "sem-nascimento" | "sem-telefone" | "mes";

const FILTROS: { key: Filtro; label: string }[] = [
  { key: "todos", label: "Todos" },
  { key: "sem-foto", label: "Sem foto" },
  { key: "sem-nascimento", label: "Sem nascimento" },
  { key: "sem-telefone", label: "Sem telefone" },
  { key: "mes", label: "Aniversariantes do mês" },
];

export default function Fotos({ onAviso }: { onAviso: (m: string) => void }) {
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState<Filtro>("todos");
  const [setor, setSetor] = useState("");
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<ApiError | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const r = await api.endomarketing.fotos();
      // Mural em ordem alfabética (src/ordem.ts).
      setColaboradores(ordenarPorNome(r.colaboradores ?? [], (c) => c.nome));
    } catch (e) {
      setErro(e instanceof ApiError ? e : new ApiError(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const setores = useMemo(
    () => ordenarTextos([...new Set(colaboradores.map((c) => c.departamento).filter(Boolean))] as string[]),
    [colaboradores],
  );

  const mesAtual = new Date().getMonth() + 1;
  const doMes = (c: Colaborador) =>
    !!c.data_nascimento && Number(String(c.data_nascimento).slice(5, 7)) === mesAtual;

  const lista = useMemo(() => {
    const q = norm(busca);
    return colaboradores.filter((c) => {
      if (setor && c.departamento !== setor) return false;
      if (q && !norm(c.nome).includes(q) && !norm(c.cargo).includes(q)) return false;
      if (filtro === "sem-foto") return !c.foto;
      if (filtro === "sem-nascimento") return !c.data_nascimento;
      if (filtro === "sem-telefone") return !temTelefone(c);
      if (filtro === "mes") return doMes(c);
      return true;
    });
  }, [colaboradores, busca, filtro, setor]);

  const comFoto = colaboradores.filter((c) => c.foto).length;
  const semNascimento = colaboradores.filter((c) => !c.data_nascimento).length;

  if (erro) return <ErrorState message={erro.message} code={erro.code} onRetry={carregar} />;

  return (
    <>
      <div className="crud-toolbar endo-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar pessoa ou cargo..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <label className="endo-mes">
          <span className="muted">Setor</span>
          <select value={setor} onChange={(e) => setSetor(e.target.value)}>
            <option value="">Todos</option>
            {setores.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <div className="toolbar-right">
          <span className="badge">
            {comFoto} de {colaboradores.length} com foto
          </span>
          {semNascimento > 0 && (
            <span className="badge badge-alerta">{semNascimento} sem nascimento</span>
          )}
        </div>
      </div>

      <div className="endo-chips">
        {FILTROS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`endo-chip ${filtro === f.key ? "ativo" : ""}`}
            onClick={() => setFiltro(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="empty">Carregando…</div>
      ) : lista.length === 0 ? (
        <div className="empty">
          {filtro === "sem-foto"
            ? "Todo mundo já tem foto. 🎉"
            : filtro === "sem-nascimento"
              ? "Nenhuma data de nascimento faltando. 🎉"
              : "Ninguém encontrado com esses filtros."}
        </div>
      ) : (
        <div className="endo-galeria">
          {lista.map((c) => (
            <CardFoto
              key={c.id}
              colaborador={c}
              aniversarianteDoMes={doMes(c)}
              onMudou={carregar}
              onAviso={onAviso}
            />
          ))}
        </div>
      )}
    </>
  );
}

function CardFoto({
  colaborador,
  aniversarianteDoMes,
  onMudou,
  onAviso,
}: {
  colaborador: Colaborador;
  aniversarianteDoMes: boolean;
  onMudou: () => Promise<void>;
  onAviso: (m: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  // Tirar a foto apaga o que está gravado no cadastro — apagar é de admin.
  // Trocar continua liberado a todos: quem enviou a errada envia a certa.
  const podeExcluir = usePodeExcluir();
  const [ocupado, setOcupado] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const [nascimento, setNascimento] = useState(colaborador.data_nascimento?.slice(0, 10) ?? "");
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    setNascimento(colaborador.data_nascimento?.slice(0, 10) ?? "");
  }, [colaborador.data_nascimento]);

  async function enviar(files: FileList | File[] | null) {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      return onAviso("Envie uma imagem (jpg, png…).");
    }
    setOcupado(true);
    try {
      const foto = await compactar(file);
      const r = await api.endomarketing.salvarFoto(colaborador.id, foto);
      onAviso(r.mensagem);
      await onMudou();
    } catch (e) {
      onAviso(e instanceof Error ? e.message : "Erro ao enviar a foto.");
    } finally {
      setOcupado(false);
    }
  }

  async function salvarNascimento(valor: string) {
    setNascimento(valor);
    try {
      await api.endomarketing.salvarNascimento(colaborador.id, valor);
      setSalvo(true);
      setTimeout(() => setSalvo(false), 1500);
      await onMudou();
    } catch (e) {
      onAviso(e instanceof Error ? e.message : "Erro ao salvar a data.");
    }
  }

  const dia = nascimento ? Number(nascimento.slice(8, 10)) : null;
  const mes = nascimento ? Number(nascimento.slice(5, 7)) : null;

  return (
    <article
      className={`endo-foto-card ${arrastando ? "arrastando" : ""} ${ocupado ? "ocupado" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setArrastando(true);
      }}
      onDragLeave={() => setArrastando(false)}
      onDrop={(e) => {
        // Arrastar a foto do explorador direto para o card: é como o RH recebe
        // as fotos (uma pasta no computador), sem abrir o seletor de arquivo.
        e.preventDefault();
        setArrastando(false);
        enviar(e.dataTransfer.files);
      }}
    >
      {aniversarianteDoMes && <span className="endo-tag-mes">🎂 este mês</span>}

      <Avatar
        pessoa={colaborador}
        tamanho="g"
        onClick={() => inputRef.current?.click()}
        titulo="Clique (ou arraste uma imagem) para trocar a foto"
      />

      <strong>{colaborador.nome}</strong>
      <small className="muted">{colaborador.cargo || colaborador.departamento || "—"}</small>

      <label className="endo-nascimento">
        <span>
          Nascimento
          {salvo && <IconCheck size={13} />}
        </span>
        <input type="date" value={nascimento} onChange={(e) => salvarNascimento(e.target.value)} />
      </label>
      {dia && mes && (
        <small className="muted endo-nasc-dica">
          faz aniversário em {String(dia).padStart(2, "0")} de {MES_CURTO[mes - 1]}
        </small>
      )}
      {!temTelefone(colaborador) && (
        <small className="endo-sem-tel">sem telefone — não recebe comunicado</small>
      )}

      <div className="endo-foto-acoes">
        <button
          className="btn-secondary btn-sm"
          onClick={() => inputRef.current?.click()}
          disabled={ocupado}
        >
          <IconUpload size={14} /> {ocupado ? "Enviando…" : colaborador.foto ? "Trocar" : "Enviar"}
        </button>
        {colaborador.foto && podeExcluir && (
          <button
            className="icon-btn icon-btn-danger"
            title="Remover a foto"
            disabled={ocupado}
            onClick={async () => {
              await api.endomarketing.salvarFoto(colaborador.id, "");
              await onMudou();
            }}
          >
            <IconTrash size={15} />
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          enviar(e.target.files);
          e.target.value = "";
        }}
      />
    </article>
  );
}
